import styled, { css } from 'styled-components';
import BaseRow from 'antd/es/row';
import type { RowProps as RowPropsType } from 'antd/es/row';
import { Property } from 'csstype';

import CustomIconBase from '@components/utils/CustomIcon';
import { PaletteColor, SizeUnit } from '@utils/types';

import BaseCol from './BaseCol';

export const Col = styled(BaseCol)<{
  zIndex?: number;
  marginbottom?: SizeUnit;
  alignItems?: Property.AlignItems;
}>`
  &&& {
    z-index: ${(p) => p.zIndex ?? undefined};
    ${(p) =>
      p.marginbottom
        ? css`
            margin-bottom: ${p.marginbottom};
          `
        : null}
    ${({ alignItems }) =>
      alignItems &&
      css`
        align-items: ${alignItems};
      `};
  }
`;

type RowProps = {
  margintype?: 'none' | 'default';
};
const marginMixin = ({ margintype }: RowProps) =>
  margintype === 'none' &&
  css`
    margin-top: 0 !important;
  `;

export const Row = styled(BaseRow)<RowProps>`
  ${marginMixin}
`;

export const BlockTitle = styled.h4`
  color: ${(p) => p.theme.palette.primaryBrand};
  font-size: 18px;
  font-weight: bold;
`;

export const Block = styled.section<{
  maxWidth?: SizeUnit;
  backgroundColor?: PaletteColor;
  padding?: SizeUnit;
}>`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  ${Row} {
    margin-top: 20px;
  }
  ${BlockTitle} {
    margin-bottom: 20px;
  }
  max-width: ${(p) => p.maxWidth ?? '820px'};
  padding: ${(p) => p.padding ?? 0};
  background-color: ${(p) => p.backgroundColor};
  width: 100%;

  .dropdown-component {
    opacity: 1;
  }
`;

export const SectionHeader = styled.div<{
  marginTop?: SizeUnit;
  marginBottom?: SizeUnit;
  overrideDesktopMargin?: SizeUnit;
}>`
  display: flex;
  align-items: flex-end;
  margin-bottom: 10px;
  margin-top: ${({ marginTop }) => marginTop};
  @media (max-width: ${(p) => p.theme.breakPoint.LARGE_1X}) {
    margin-bottom: ${({ marginBottom }) => marginBottom ?? '-12px'};
    align-items: center;
    & > span:first-child {
      align-self: flex-start;
    }
  }
`;

export const Section = styled.section<{
  color?: PaletteColor;
  variant?: 'payment';
  blur?: boolean;
}>`
  background-color: ${({ theme }) => theme.palette.white};
  ${(p) =>
    p.variant === 'payment' &&
    css`
      display: flex;
      flex-direction: column;
      min-height: 91px;
      opacity: ${p.blur ? 0.3 : 1};
      transition: opacity 0.2s ease;
      ${SectionHeader} {
        margin-top: 13.5px;
      }
    `}
  color: ${({ color, theme }) => color && theme.palette[color]};
`;

export const SectionTitle = styled.h3<{ variant?: 'payment' }>`
  color: ${(p) => p.theme.palette.darkGrey};
  letter-spacing: 1px;
  font-weight: bold;
  vertical-align: bottom;
  text-align: center;
  margin-left: 3px;
  ${(p) =>
    p.variant === 'payment' &&
    css`
      font-size: 24px;
      margin: 0 0 0 15px;
      line-height: 1;
      @media (max-width: ${(p) => p.theme.breakPoint.LARGE_1X}) {
        font-size: 18px;
      }
    `}
`;

export const CustomIcon = styled(CustomIconBase)`
  display: flex;
  align-items: flex-end;
`;

export interface ITextAreaBox {
  height?: SizeUnit;
  hasError?: boolean;
}

export const TextAreaBox = styled.textarea<ITextAreaBox>`
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  width: 100%;
  height: ${(p) => p.height ?? '100%'};
  border-radius: 4px;
  border: solid 1px
    ${({ theme, hasError }) =>
      hasError ? theme.palette.coral : theme.palette.inputBorderColor};
  background-color: ${(p) => p.theme.palette.white};
  color: ${(p) => p.theme.palette.grey1100};
  padding: 12px;
  box-sizing: border-box;
  caret-color: ${({ theme }) => theme.palette.black};

  &:placeholder {
    color: ${(p) => p.theme.palette.grey800};
  }

  &:is(:focus-within, :hover) {
    border: 1px solid
      ${({ theme, hasError }) =>
        hasError
          ? theme.palette.errorColor
          : theme.palette.inputBorderColorActive};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.5;
  }
`;

export const BillingCheckBoxLabel = styled.span`
  padding: 0 8px;
  color: ${(p) => p.theme.palette.brownishGrey};
  font-size: 15px;
  font-weight: bold;
  line-height: 1.57px;
  & > span {
    color: ${(p) => p.theme.palette.primaryBrand};
  }
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    font-size: 13px;
  }
`;

export const MarkdownAnchorStyle = css`
  color: ${(p) => p.theme.palette.primaryBrand};
  cursor: pointer;
  margin-right: 3px;
  text-decoration: underline;
  font-weight: bold;

  &:hover {
    color: ${({ theme }) => theme.palette.primaryBrand};
    text-decoration: none;
    opacity: 0.75;
  }
`;

export const ImportantText = styled.a`
  ${MarkdownAnchorStyle}
`;

export const Span = styled.span`
  cursor: pointer;
  font-size: 12px;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const Paragraph = styled.p`
  white-space: pre-line;
  margin: 0;
  padding-left: 30px;
  font-size: 13px;
`;
export const ContractText = styled.p`
  white-space: pre-line;
  margin: 0;
  font-size: 13px;
`;

export const ContractTextDistanceSelling = styled.p`
  white-space: pre-line;
  margin: 0;
  max-height: 36px;
`;

export const gutter: RowPropsType['gutter'] = [
  16,
  { xs: 20, sm: 20, md: 20, lg: 20 },
];

export const MarkdownText = styled.span`
  font-size: 13px;
  white-space: pre-line;
  margin: 0;
`;
